import { type FC } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';

import clsx from 'clsx';

import { updateAdClick } from '@/shared/api/ad';
import { openLink } from '@/shared/helpers/telegram/share/open-link';
import { useIsInViewport } from '@/shared/hooks/use-is-in-viewport';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { clickAdEvent } from '@/shared/service/analytics/events/click-ad-event';
import { showAdEvent } from '@/shared/service/analytics/events/show-ad-event';

import { AD_BANNER_HEIGHT_BY_SIZE } from '../../constants';
import { useWrapperAdsActions } from '../../model/wrapper-ads.store';
import { type Ad, AdType } from '../../model/wrapper-ads.types';
import { AdCard } from '../ad-card';

import s from './index.module.scss';

interface Props {
  ad: Ad;
  className?: string;
}

export const AdBanner: FC<Props> = ({ ad, className }) => {
  const [containerRef, isInViewport] = useIsInViewport<HTMLButtonElement>();

  const { afterShowCallback } = useWrapperAdsActions();

  const [isEventSent, setIsEventSent] = useState(false);

  const handleClick = async () => {
    if (!ad) return;

    const e = clickAdEvent(ad);
    AnalyticsService.send(e);

    await updateAdClick(ad.id);

    openLink(ad.link);
  };

  useEffect(() => {
    // Skip event if the add was shown before
    if (isEventSent) return;

    if (isInViewport) {
      setIsEventSent(true);
      afterShowCallback(ad);

      const e = showAdEvent(ad);

      AnalyticsService.send(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport]);

  return (
    <button
      ref={containerRef}
      className={clsx(s.container, s[ad.type.toLowerCase()], className)}
      {...(ad.type === AdType.BANNER
        ? {
            onClick: handleClick,
            style: { height: `${AD_BANNER_HEIGHT_BY_SIZE[ad.size]}px` },
          }
        : {})}
    >
      {ad.type === AdType.BANNER && (
        <img className={s.img} src={ad.image} alt="Ad preview" />
      )}
      {ad.type === AdType.BLOCK && (
        <AdCard className={className} handleClick={handleClick} ad={ad} />
      )}
    </button>
  );
};
